import {registerBrick} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core';
import {
    jsonToSxProps,
    useMUITheme,
    colorExists,
    validateString,
    validateVariant,
} from '@olympeio/core';
import {ThemeProvider} from '@mui/material/styles';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import React from 'react';
import MUIButton from '@mui/material/Button';
import {Badge, Tooltip} from '@mui/material';

const FILTER_ICON_SX = Object.freeze({color: '#55606a'});

/**
 * Clickable component to trigger an action, wrapped in a Badge component
 */
export default class BadgeButton extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    updateParent(parent, element) {
        parent.style.overflow = 'visible';
        return super.updateParent(parent, element);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            // eslint-disable-next-line react/prop-types
            const [hidden] = props.values;
            const theme = useMUITheme($);

            const fontSize = useProperty($, 'Font Size');
            const displayBadge = useProperty($, 'Display Badge');
            const badgeColor = useProperty($, 'Badge Color');
            const badgeTooltip = useProperty($, 'Badge Tooltip');
            const badgeSxJSON = useProperty($, 'MUI sx for badge [json]');

            const customSx = {
                'border': '1px solid #c7c7c7',
                'borderRadius': `4px`,
                'backgroundColor': '#f5f5f5',
                '&:hover': {'backgroundColor': '#f5f5f5', 'border': '1px solid black'},
            };

            const sizeVariants = ['medium', 'small', 'large'];
            const validatedSizeVariant = validateVariant(fontSize, 'Font Size', 'Button', sizeVariants);

            const button = (
                <MUIButton
                    data-cy={'filter-button'}
                    variant="outlined"
                    onClick={() => $.trigger('On Click')}
                    startIcon={<FilterAltOutlinedIcon sx={FILTER_ICON_SX} />}
                    size={validatedSizeVariant}
                    sx={{
                        minHeight: '39px',
                        color: '#55606a',
                        textTransform: 'none',
                        ...customSx,
                    }}
                >
                    {validateString(useProperty($, 'Text'), 'Text', 'Button') ?? 'Filter'}
                </MUIButton>
            );

            const wrappedButton = displayBadge ? (
                <Tooltip placement="top" title={badgeTooltip || ''}>
                    <Badge
                        variant="dot"
                        overlap="rectangular"
                        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                        color={colorExists(theme, badgeColor, 'Badge') ? badgeColor : 'primary'}
                        sx={{
                            '& .MuiBadge-badge': {
                                top: '5px',
                                right: '3px',
                                width: '12px',
                                height: '12px',
                                minWidth: '12px',
                                borderRadius: '50%',
                            },
                            ...jsonToSxProps(badgeSxJSON),
                        }}
                    >
                        {button}
                    </Badge>
                </Tooltip>
            ) : (
                button
            );

            return (
                !hidden && (
                    <ThemeProvider theme={theme}>
                        {wrappedButton}
                    </ThemeProvider>
                )
            );
        };
    }
}

registerBrick('0193b6392e9881d0d47c', BadgeButton);
